/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   licenceSession.ts
 *     Description     :   Spec 286 US4 — the licence enters the SPA two ways (contracts/portal-gating.md
 *                         "SPA"): from GET /api/auth/session (applySessionLicence), and from a later
 *                         call the backend refused for the licence — 403 LICENSE_INACTIVE or 503
 *                         LICENSE_UNVERIFIABLE (applyLicenceRefusal) — because the licence can change
 *                         mid-session. Both land in the auth store, which LicenseGate renders from.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import { NO_LICENSES, type PlugReason } from '@shared/access/licenses';
import type { SessionInfo } from '@shared/types/auth';
import { useAuthStore, type LicenceRefusal } from '../stores/authStore';
import { trackException } from '../services/telemetryService';

const PLUG_REASONS: readonly PlugReason[] = ['status-suspended', 'status-revoked', 'unknown-value', 'no-row'];

/** Store what a session read said: the resolved licence and its add-ons (fail-closed when absent). */
export function applySessionLicence(session: Pick<SessionInfo, 'license' | 'licenses'>): void {
  const { setLicense, setLicenses } = useAuthStore.getState();
  setLicense(session.license ?? null);
  setLicenses(session.licenses ?? NO_LICENSES);
}

/** The licence refusal an API error carries, or null for any other error. */
export function licenceRefusalOf(error: unknown): LicenceRefusal | null {
  const response = (error as { response?: { status?: number; data?: { error?: unknown; reason?: unknown } } })?.response;
  const code = response?.data?.error;
  if (response?.status === 503 && code === 'LICENSE_UNVERIFIABLE') return { state: 'unverifiable' };
  if (response?.status === 403 && code === 'LICENSE_INACTIVE') {
    const reason = response.data?.reason;
    return {
      state: 'plugged',
      plugReason: PLUG_REASONS.includes(reason as PlugReason) ? (reason as PlugReason) : 'unknown-value',
    };
  }
  return null;
}

/**
 * The one retry rule for a licence refusal, on every client that retries (review round 3, batch 2): never. The answer
 * has already switched the SPA, and a 503 LICENSE_UNVERIFIABLE retried would ask a backend whose licence read is
 * failing three more times for the answer it just gave.
 */
export function isLicenceRefusal(error: unknown): boolean {
  return licenceRefusalOf(error) !== null;
}

/** Apply a licence refusal to the store. Returns true when the error was one (the caller then stops). */
export function applyLicenceRefusal(error: unknown): boolean {
  const refusal = licenceRefusalOf(error);
  if (!refusal) return false;
  useAuthStore.getState().licenceRefused(refusal);
  return true;
}

/** The fetch signature the observer wraps (the DOM lib's fetch). */
type FetchFn = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/**
 * Marks the installed wrapper itself, under a registry symbol (review round 1 of batch 2): a marker on the HOST
 * survived a restore of the plain fetch, so the observer was gone and never re-installed; and a re-evaluated module
 * (Vite HMR) would have stacked a second wrapper. Symbol.for is shared across module instances.
 */
const OBSERVER_MARK = Symbol.for('fcp.licenceFetchObserver');

/**
 * The URL fetch() requests for an input, by fetch's own rule: a Request's URL, and the string form of anything else
 * (review round 2 #7, round 3 #10: a JavaScript caller passing undefined requests '/undefined' here).
 */
function requestedUrl(input: unknown): string {
  return typeof Request !== 'undefined' && input instanceof Request ? input.url : String(input);
}

/**
 * The origins whose answers can carry the portal's licence refusal: this page's, and the API's when it is configured
 * on another one (dev serves the SPA and the API on different ports). A foreign 403/503 — Application Insights
 * throttling, a CDN, another app — is never read, so a coincidental body can never lock the shell.
 */
function isOurApi(url: string): boolean {
  try {
    const origin = new URL(url, window.location.href).origin;
    if (origin === window.location.origin) return true;
    const apiBase = import.meta.env.VITE_API_BASE_URL;
    return !!apiBase && origin === new URL(apiBase, window.location.href).origin;
  } catch {
    return false;
  }
}

/**
 * Reads a refusal out of a cloned answer, after the caller already has the original. A body that is not JSON (or a
 * read the caller aborted) is no refusal. A failure while switching the store is a defect (review round 2 #3): the
 * caller cannot be told any more, so it goes to telemetry, which also writes it to the console — never swallowed.
 */
async function readRefusal(status: number, clone: Response): Promise<void> {
  let data: unknown;
  try {
    data = await clone.json();
  } catch {
    return;
  }
  try {
    applyLicenceRefusal({ response: { status, data } });
  } catch (error) {
    reportFailure(error);
  }
}

/**
 * Senior review 2026-10-10 #9: many modules call fetch() directly, so a licence refused mid-session on one of those
 * calls never reached the store and the user stayed on a shell that could no longer load anything. One observer on
 * fetch covers them all, future callers included: a 403 or 503 JSON answer from our API goes to applyLicenceRefusal in
 * the shape the axios interceptors pass. The caller gets the response exactly when and as it would without the
 * observer (review round 3 #5): the refusal is read from a clone in the background, and a body that cannot be cloned
 * (already read or locked elsewhere, review round 4 #1) is simply not read. Only a real Response is read, only with
 * one of those two statuses, only when it declares JSON, and only when its final URL — after any redirect (review
 * round 3 #4) — is our API; a 200 (a streamed SSE answer included) is never cloned. A test double answering undefined
 * or a plain object passes through as it came. A host with no fetch is left alone (review round 3 #6), and one whose
 * fetch cannot be replaced keeps working unobserved, the failure reported (review round 4 #4): the observer must never
 * stop the portal booting.
 */
export function installLicenceFetchObserver(host: { fetch?: FetchFn }): void {
  const current = host.fetch;
  if (typeof current !== 'function' || OBSERVER_MARK in current) return;
  const original = current.bind(host);
  const observed = Object.assign(
    async (...args: Parameters<FetchFn>): Promise<Response> => {
      const response = await original(...args);
      if (
        response instanceof Response
        && (response.status === 403 || response.status === 503)
        && (response.headers.get('content-type') ?? '').includes('json')
        && isOurApi(response.url || requestedUrl(args[0]))
      ) {
        try {
          void readRefusal(response.status, response.clone());
        } catch (error) {
          // Not clonable (its body was read or locked elsewhere): the caller keeps its answer, and the switch this
          // answer may carry is reported as missed rather than dropped in silence (delta review D3).
          reportFailure(error);
        }
      }
      return response;
    },
    { [OBSERVER_MARK]: true },
  );
  try {
    host.fetch = observed;
  } catch (error) {
    reportFailure(error);
    return;
  }
  // An accessor can ignore the assignment without throwing (delta review D2).
  if (host.fetch !== observed) {
    reportFailure(new Error('the licence fetch observer could not replace fetch; direct fetch() callers are unobserved'));
  }
}

function reportFailure(error: unknown): void {
  trackException(error instanceof Error ? error : new Error(String(error)));
}
